import { memoize, uniqueByKey } from '@ls/js-utils';
import { getFlattenedNodes, SummaryType } from '@ls/grouping-service';
import SelectionState from 'state/selections/SelectionState';
import scope from "./scope"
import { createSelector } from 'reselect';
import { isDefined } from '@ls/js-utils';
import CalcType from 'state/Constants/CalcType';

export const selectData = (summaryType) => state => state[scope]['data'][summaryType];
export const selectFetchingData = (summaryType) => state => state[scope]['fetchingData'][summaryType];
export const selectFetchingScenarioAnalysisSbsData = (summaryType) => (state) =>
    state[scope]["fetchingScenarioAnalysisSbsData"][summaryType];
export const selectScenarioAnalysisSbsData = (summaryType) => (state) =>
    state[scope]["scenarioAnalysisSbsData"][summaryType];
export const selectSelectedAccountEntityId = (selectionState) => state => state.selection[selectionState].accountEntityId;
export const selectSelectedBenchmarkEntityId = (selectionState) => state => state.selection[selectionState].benchmarkEntityId;

export const selectDefinitions = memoize((selectionState) => {
    if (selectionState === SelectionState.UNCOMMITTED) {
        return state => state[scope]['definitions'][selectionState];
    }
    return createSelector(
        state => state[scope]['definitions'][selectionState],
        (definitions) => {
            const completeDefinitions = definitions.filter(({ complete }) => complete);
            return completeDefinitions.filter(uniqueByKey(completeDefinitions, 'key'));
        },
    );
});

export const selectRowMap = createSelector(
    selectDefinitions(SelectionState.COMMITTED),
    selectData(SummaryType.ABSOLUTE),
    selectData(SummaryType.RELATIVE),
    selectSelectedAccountEntityId(SelectionState.COMMITTED),
    selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
    (definitions, absData, relData, accEntityId, bmkEntityId) => {
        // const nodes = getFlattenedNodes(scenarioAnalysisData?.root) ?? [];
        const absoluteNodes = getFlattenedNodes(absData?.root) ?? [];
        const relativeNodes = getFlattenedNodes(relData?.root) ?? [];
        return [...absoluteNodes, ...relativeNodes].reduce((rowMap, node) => {
            const { pathString } = node;
            rowMap[pathString] = rowMap[pathString] ?? { pathString };
            const row = rowMap[pathString];
            definitions
                .filter(({ key }) => !row[key])
                .forEach((definition) => {
                    row[definition.key] = findScenarioValue(
                        node,
                        definition,
                        accEntityId,
                        bmkEntityId,
                    );
                });
            return rowMap;
        }, {})
    },
);

export const selectRowSbsMap = createSelector(
    selectDefinitions(SelectionState.COMMITTED),
    selectScenarioAnalysisSbsData(SummaryType.ABSOLUTE),
    selectScenarioAnalysisSbsData(SummaryType.RELATIVE),
    selectSelectedAccountEntityId(SelectionState.COMMITTED),
    selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
    (definitions, absSbsData, relSbsData, accEntityId, bmkEntityId) => {
        const absoluteSbsNodes = getFlattenedNodes(absSbsData?.root) ?? [];
        const relativeSbsNodes = getFlattenedNodes(relSbsData?.root) ?? [];
        return [...absoluteSbsNodes, ...relativeSbsNodes].reduce((rowMap, node) => {
            const { pathString } = node;
            rowMap[pathString] = rowMap[pathString] ?? { pathString };
            const row = rowMap[pathString];
            definitions
                .filter(({ key }) => !row[key])
                .forEach((definition) => {
                    row[`${definition.key} - scenario`] = findScenarioValue(
                        node,
                        definition,
                        accEntityId,
                        bmkEntityId,
                    );
                });
            return rowMap;
        }, {})
    },
);

export const getEntitySummariesObject = (node, entityId, isRelative) => {
    if (isDefined(node?.securityId)) {
        const entityResultMetaData = node?.positionSummary?.entityResultMetaData ?? {};
        if (entityResultMetaData.entityId === entityId && entityResultMetaData.relative === isRelative) {
            return node.positionSummary;
        }
    } else {
        return node?.entitySummaries?.find(({ entityResultMetaData = {} }) =>
            entityResultMetaData.entityId === entityId && entityResultMetaData.relative === isRelative
        );
    }

}

const findScenarioValue = (node, definition, accEntityId, bmkEntityId) => {
    const {
        scenarioAnalysisId,
        scenarioAnalysisFactor,
        calcType,
        scenarioType
    } = definition;
    const entityId = calcType === CalcType.Benchmark ? bmkEntityId : accEntityId;
    const isRelative = calcType === CalcType.Relative;


    const entitySummariesObject = getEntitySummariesObject(node, entityId, isRelative) ?? {};

    const summaryType = `values${scenarioType}`

    const scenarioSummaries = entitySummariesObject['scenarios'];
    const scenarioSummaryObject = scenarioSummaries?.find(scenarioSummary => {
        return scenarioSummary?.primaryKey?.scenarioId === scenarioAnalysisId

    });
    return scenarioSummaryObject?.[summaryType]?.[scenarioAnalysisFactor?.toLowerCase()];
};

